import React, { memo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/History.js";

interface GridViewCardProps {
  title: string;
  updateDate: string;
  onClick?: () => void;
  menuItems?: Array<{
    label: string;
    onClick: () => void;
  }>;
  TitleIcon?: React.ComponentType<{ size?: "lg" | "md" | "xl" | "2xl" }>;
}

// OPTIMIZED: Memoized to prevent unnecessary re-renders
export const GridViewCard: React.FC<GridViewCardProps> = memo(({
  title,
  updateDate,
  onClick,
  TitleIcon,
}) => {

  const formatDate = (dateString: string) => {
    // Return '-' if dateString is empty or invalid
    if (!dateString || dateString.trim() === "") {
      return "-";
    }

    const date = new Date(dateString);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "-";
    }

    return date
      .toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/\//g, "/");
  };

  return (
    <Card
      data-testid="grid-view-card"
      sx={(theme) => ({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: onClick ? "pointer" : "default",
        transition: theme.transitions.create(["box-shadow"], {
          duration: theme.transitions.duration.shorter,
        }),
        "&:hover": {
          boxShadow: 3,
        },
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        backgroundColor: "background.paper",
        px: 1.5,
      })}
      onClick={onClick}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          px: 0.5,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Header with icon, title and menu */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
          }}
        >
          {/* Icon */}
          {TitleIcon && (
            <Box
              sx={(theme) => ({
                backgroundColor: "grey.100",
                borderRadius: theme.spacing(1.5),
                padding: theme.spacing(1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              })}
            >
              <TitleIcon size="lg" />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Tooltip title={title} arrow placement="top">
              <Typography
                variant="h4"
                component="h3"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textDecoration: "underline",
                }}
              >
                {title}
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        {/* Last Update Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            mt: "auto",
          }}
        >
          {/* Clock Icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              mt: 0.5,
            }}
          >
            <HistoryIcon size="lg" />
          </Box>

          {/* Last Update Text */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              flex: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={(theme) => ({
                fontWeight: 600,
                color: "text.primary",
                letterSpacing: theme.typography.pxToRem(0.3),
              })}
            >
              Last update
            </Typography>
            <Typography
              variant="caption"
              sx={(theme) => ({
                color: "text.primary",
                letterSpacing: theme.typography.pxToRem(0.2),
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              })}
            >
              {formatDate(updateDate)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

export default GridViewCard;
